

import {
    Settings2, ChevronLeft, ExternalLink
} from 'lucide-react';
import { useCallback, useMemo, memo } from 'react';
import './animations.css';
import { QuickAction } from '../quick-actions';
import { cn } from '@/src/lib/shadcn/cn';
import { accountSettings } from './account-settings';
import AddResources from './add-resources';
import Integrations from './integrations';
import { useRouter } from 'next/navigation';

interface MoreOptionsProps {
    onBack: () => void;
    onClose: () => void;
    searchQuery: string;
}

const filterMoreOptions = (items: QuickAction[], query: string) => {
    if (!query) return items;
    const lowerQuery = query.toLowerCase();
    return items.filter(item =>
        item.text?.toLowerCase().includes(lowerQuery) ||
        item.description?.toLowerCase().includes(lowerQuery)
    );
};

function OptionButton({ option, index, onOptionClick }: { option: QuickAction; index: number; onOptionClick: (option: QuickAction) => void }) {
    const handleClick = useCallback(() => {
        onOptionClick(option);
    }, [option, onOptionClick]);

    const handleNewTabClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (option.newTabHref) {
            window.open(option.newTabHref, '_blank');
        }
    }, [option]);

    return (
        <button
            key={index}
            data-testid={`spotlight-option-${option.text?.toLowerCase().replace(/\s+/g, '-')}`}
            className={cn(
                "flex items-start gap-3 w-full text-left px-3 py-2 rounded-lg group",
                "hover:bg-gray-100 dark:hover:bg-gray-700/50",
                "text-gray-700 dark:text-gray-300 text-sm",
                "more-option-item more-option-item-visible",
                option.disabled && "opacity-50 cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent"
            )}
            onClick={handleClick}
            disabled={option.disabled}
        >
            <span className="mt-0.5 text-gray-500 dark:text-gray-400" data-testid={`spotlight-option-icon-${option.text?.toLowerCase().replace(/\s+/g, '-')}`}>{option.icon}</span>
            <div className="flex-1" data-testid={`spotlight-option-content-${option.text?.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between" data-testid={`spotlight-option-header-${option.text?.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="flex items-center gap-2" data-testid={`spotlight-option-text-container-${option.text?.toLowerCase().replace(/\s+/g, '-')}`}>
                        {option.text}
                        {option.badge && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full" data-testid={`spotlight-option-badge-${option.text?.toLowerCase().replace(/\s+/g, '-')}`}>
                                {option.badge}
                            </span>
                        )}
                    </div>
                    {option.newTabHref && !option.disabled && (
                        <button
                            data-testid={`spotlight-option-external-link-${option.text?.toLowerCase().replace(/\s+/g, '-')}`}
                            onClick={handleNewTabClick}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                        >
                            <ExternalLink className="w-3 h-3 text-gray-500" />
                        </button>
                    )}
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400" data-testid={`spotlight-option-description-${option.text?.toLowerCase().replace(/\s+/g, '-')}`}>{option.description}</span>
            </div>
        </button>
    );
}

const MemoizedOptionButton = memo(OptionButton);

export default function MoreOptions({ onBack, onClose, searchQuery }: MoreOptionsProps) {
    const router = useRouter();
    const filteredAccountSettings = useMemo(() => 
        filterMoreOptions(accountSettings, searchQuery),
        [searchQuery]
    );

    const handleOptionClick = useCallback((option: QuickAction) => {
        if (option.disabled) return;

        if (option.href) {
            router.push(option.href);
            onClose();
            return;
        }

        switch (option.text) {
            case "Users Management":
                window.open('/users', '_blank');
                break;
        }
        onClose();
    }, [router, onClose]);

    return (
        <div className="space-y-6" data-testid="spotlight-more-options">
            <div className="flex items-center justify-between" data-testid="spotlight-more-options-header">
                <div className="flex items-center gap-2">
                    <button
                        data-testid="spotlight-more-options-back-button"
                        onClick={onBack}
                        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back
                    </button>
                    <div className="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-2" data-testid="spotlight-more-options-divider" />
                    <div className="flex items-center gap-2" data-testid="spotlight-more-options-title-container">
                        <Settings2 className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-testid="spotlight-more-options-title">
                            More Options
                        </h3>
                    </div>
                </div>
            </div>

            <div className="space-y-6" data-testid="spotlight-more-options-sections">
                {/* Add Resources Section */}
                <AddResources searchQuery={searchQuery} onClose={onClose} />

                {/* Integrations Section */}
                <Integrations searchQuery={searchQuery} onClose={onClose} />

                {/* Account & Settings Section */}
                {filteredAccountSettings.length > 0 && (
                    <div data-testid="spotlight-option-category-account-settings">
                        <div className="flex items-center gap-3" data-testid="spotlight-option-category-account-settings-header">
                            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-testid="spotlight-option-category-account-settings-title">
                                Account & Settings
                            </h3>
                            <div className="h-[1px] flex-1 bg-gradient-to-r from-gray-200 to-transparent dark:from-gray-700" data-testid="spotlight-option-category-account-settings-divider" />
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2" data-testid="spotlight-option-category-account-settings-items">
                            {filteredAccountSettings.map((option, index) => (
                                <MemoizedOptionButton 
                                    key={index}
                                    option={option}
                                    index={index}
                                    onOptionClick={handleOptionClick}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
} 